import React, { useEffect, useRef, useState } from 'react';

/**
 * VersionUpdateNotifier
 *
 * Polls /version.json on an interval. When the deployed build version
 * changes (i.e. a new CI/CD deploy has finished), shows a non-blocking
 * toast asking the user to reload. Auto-reloads after AUTO_RELOAD_SECONDS
 * unless dismissed.
 *
 * /version.json is regenerated by scripts/generate-version.js on every
 * `npm run build`, and served fresh because of <meta http-equiv> cache
 * headers + the cache-busting query string we append below.
 */

const POLL_INTERVAL_MS = 60 * 1000;          // 60 s
const AUTO_RELOAD_SECONDS = 30;              // countdown shown to user
const VERSION_URL = '/version.json';

const fetchVersion = async () => {
  try {
    // Cache-bust both browser cache & any intermediate caches
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.version || null;
  } catch {
    return null;
  }
};

const VersionUpdateNotifier = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_RELOAD_SECONDS);
  const builtVersionRef = useRef(null);
  const dismissedRef = useRef(false);

  // Capture the version at app load (this is the version the user is currently running)
  useEffect(() => {
    let cancelled = false;
    fetchVersion().then((v) => {
      if (!cancelled && v) builtVersionRef.current = v;
    });
    return () => { cancelled = true; };
  }, []);

  // Poll for newer version
  useEffect(() => {
    const interval = setInterval(async () => {
      if (dismissedRef.current || updateAvailable) return;
      const latest = await fetchVersion();
      if (!latest) return;
      if (!builtVersionRef.current) {
        builtVersionRef.current = latest;
        return;
      }
      if (latest !== builtVersionRef.current) {
        setUpdateAvailable(true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [updateAvailable]);

  // Countdown → auto reload
  useEffect(() => {
    if (!updateAvailable) return undefined;
    setCountdown(AUTO_RELOAD_SECONDS);
    const tick = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(tick);
          // Force a fully-fresh load — bypass HTTP cache for index.html
          window.location.reload();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [updateAvailable]);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 2000,
        maxWidth: 360,
        background: 'linear-gradient(135deg, #0f3a3f 0%, #1f656c 100%)',
        color: '#fff',
        padding: '16px 18px',
        borderRadius: 14,
        boxShadow: '0 18px 40px rgba(15,23,42,0.28)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: '#34d399',
            boxShadow: '0 0 0 6px rgba(52,211,153,0.25)',
            animation: 'crmPulse 1.2s ease-in-out infinite',
          }}
        />
        <strong style={{ fontSize: 14 }}>New version available</strong>
      </div>
      <div style={{ opacity: 0.92 }}>
        A fresh build has been deployed. Reloading in {countdown}s to pick up the latest changes.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 8,
            border: 'none',
            background: '#fff',
            color: '#0f3a3f',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Reload now
        </button>
        <button
          type="button"
          onClick={() => {
            dismissedRef.current = true;
            setUpdateAvailable(false);
          }}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.4)',
            background: 'transparent',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          Later
        </button>
      </div>
    </div>
  );
};

export default VersionUpdateNotifier;
